import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { AuditTrailPanel } from '../../../components/resource-detail/audit-trail-panel.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteRatePlan } from '../api/mutations.js';
import { useRatePlanDetail } from '../api/queries.js';
import { type RatePlanDetail } from '../api/schemas.js';

function formatDate(value: string | null): string {
  if (value === null) return '—';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatNumber(value: number | null): string {
  return value === null ? '—' : String(value);
}

export function RatePlanDetailPage(): React.JSX.Element {
  const { ratePlanId } = useParams();
  const { search } = useLocation();
  const query = useRatePlanDetail(ratePlanId ?? '');
  const softDelete = useSoftDeleteRatePlan();
  const [confirming, setConfirming] = useState(false);

  return (
    <ResourceDetailPage<RatePlanDetail>
      query={query}
      permission={PERMISSIONS.PRICING_READ}
      parent={{ to: '/rate-plans', label: 'Rate plans' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.PRICING_UPDATE}>
            <Button asChild variant="secondary">
              <Link to={{ pathname: `/rate-plans/${ratePlanId ?? ''}/edit`, search }}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit
              </Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.PRICING_UPDATE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
            </DetailField>
            <DetailField label="Currency">{data.currency}</DetailField>
            <DetailField label="Meal plan">{data.mealPlan}</DetailField>
            <DetailField label="Refundable">{data.refundable ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Property">
              <Link className="underline" to={`/properties/${data.propertyId}`}>
                View property
              </Link>
            </DetailField>
            <DetailField label="Minimum stay">{formatNumber(data.minLengthOfStay)}</DetailField>
            <DetailField label="Maximum stay">{formatNumber(data.maxLengthOfStay)}</DetailField>
            <DetailField label="Advance booking days">
              {formatNumber(data.advanceBookingDays)}
            </DetailField>
            <DetailField label="Active from">{formatDate(data.activeFrom)}</DetailField>
            <DetailField label="Active to">{formatDate(data.activeTo)}</DetailField>
            {data.pmsRateId !== null ? (
              <DetailField label="PMS rate id">{data.pmsRateId}</DetailField>
            ) : null}
            <DetailField label="Version">{String(data.version)}</DetailField>
          </DetailGrid>

          <AuditTrailPanel entityType="rate_plan" entityId={data.id} />

          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title="Delete rate plan"
            description={`${data.name} will be removed from this list. Existing reservations and rates that use it remain in place.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              softDelete.mutate(data.id, {
                onSuccess: () => {
                  setConfirming(false);
                },
              });
            }}
          />
        </>
      )}
    </ResourceDetailPage>
  );
}
